<template>
  <div class="wrapper m-10">
    <div v-if="state.currentBankIdState === BankIDState.NO_CLIENT">
      <div v-if="openOnSameDevice">
        <div class="flex justify-center">
          <AppButton button-type="button" @on-click="autostart">
            Starta BankID
            <nuxt-icon name="bankId" aria-hidden="true" />
          </AppButton>
        </div>
      </div>
      <template v-else>
        <div class="flex justify-center">
          <AppButton button-type="button" @on-click="showQr">
            {{ isSign ? 'Signera' : 'Identifiera' }} med BankID
          </AppButton>
        </div>
      </template>
    </div>

    <div
      v-if="state.currentBankIdState === BankIDState.OUTSTANDING_TRANSACTION"
    >
      <template v-if="openOnSameDevice">
        <div class="flex justify-center">
          <AppButton button-type="button" @on-click="autostart">
            Starta BankID
            <nuxt-icon name="bankId" aria-hidden="true" />
          </AppButton>
        </div>

        <div
          class="loader flex justify-center content-center gap-8 max-w-[80%] m-auto pt-16"
        >
          <nuxt-icon
            name="refresh"
            alt="laddningssymbol"
            class="text-[2.4rem] self-center w-24 mr-[1px] [&>*]:animate-spin-slow-ease [&>*]:motion-reduce:animate-none"
          />
          <span>
            <p>Försöker starta BankID-appen.</p>
          </span>
        </div>
      </template>
      <div
        v-else
        ref="qrSectionRef"
        tabindex="-1"
        role="group"
        aria-label="BankID QR-kod"
        class="focus:outline-none"
      >
        <StyledOrderedList :items="bankIdHelpDescTexts">
          <template #default="slotProps">
            <p>{{ slotProps.item }}</p>
          </template>
        </StyledOrderedList>

        <div class="flex justify-center align-middle text-text-primary">
          <NuxtImg
            class="max-w-200"
            :src="'data:image/png;base64,' + qrData"
            alt="QR kod för bankid"
          />
        </div>
        <div
          class="loader flex justify-center content-center gap-8 max-w-[80%] m-auto"
        >
          <nuxt-icon
            name="refresh"
            alt="laddningssymbol"
            class="text-text-primary text-[2.4rem] self-center w-24 mr-[1px] [&>*]:animate-spin-slow-ease [&>*]:motion-reduce:animate-none"
          />
          <span>
            <p>
              Starta BankID-programmet. Om du lämnar den här sidan avbryts
              inloggningen.
            </p>
          </span>
        </div>
      </div>
    </div>
    <div v-if="state.currentBankIdState === BankIDState.USER_SIGN">
      <StyledOrderedList :items="bankIdHelpDescTexts">
        <template #default="slotProps">
          <p>{{ slotProps.item }}</p>
        </template>
      </StyledOrderedList>
      <div
        class="loader flex justify-center content-center gap-8 max-w-[80%] m-auto py-24"
      >
        <nuxt-icon
          name="refresh"
          alt="laddningssymbol"
          class="text-[2.4rem] self-center w-24 mr-[1px] [&>*]:animate-spin-slow-ease [&>*]:motion-reduce:animate-none"
        />
        <span>
          <p>
            Starta BankID-programmet. Om du lämnar den här sidan avbryts
            inloggningen.
          </p>
        </span>
      </div>
    </div>
    <div v-if="state.currentBankIdState === BankIDState.COMPLETE">
      <p v-if="!customErrorMessage">Väntar...</p>
      <div aria-live="polite" aria-atomic="true">
        <p class="sr-only">
          {{ customErrorMessage }}
        </p>
        <div v-if="customErrorMessage">
          <AppNotification border-color="border-border-error text-center">
            <span class="pb-8 flex flex-row gap-8">
              <nuxt-icon
                class="text-24"
                filled
                name="alert-circle-red"
                aria-hidden="true"
              />
              <p class="heading-xxs text-left">
                {{ customErrorMessage }}
              </p>
            </span>

            <div class="flex justify-start">
              <AppButton @on-click="openOnSameDevice ? autostart() : auth()">
                Försök igen
              </AppButton>
            </div>
          </AppNotification>
        </div>
      </div>
    </div>

    <div aria-live="polite" aria-atomic="true">
      <p class="sr-only">
        {{
          state.currentBankIdState === BankIDState.USER_CANCEL
            ? 'Något blev fel vid verifieringen. Åtgärden avbruten.'
            : ''
        }}
      </p>
      <div
        v-if="state.currentBankIdState === BankIDState.USER_CANCEL"
        class="flex justify-center"
      >
        <!-- todo: This needs to be a nice error message with the suggested copy from bankId docs -->
        <AppNotification
          v-model="showError"
          border-color="border-border-error text-center"
        >
          <span class="pb-8 flex flex-row gap-8" aria-hidden="true">
            <nuxt-icon
              class="text-24"
              filled
              name="alert-circle-red"
              aria-hidden="true"
            />
            <p class="heading-xxs text-left">
              Något blev fel vid verifieringen. Åtgärden avbruten. Försök igen.
            </p>
          </span>

          <div class="flex justify-start">
            <AppButton @on-click="openOnSameDevice ? autostart() : auth()">
              Försök igen
            </AppButton>
          </div>
        </AppNotification>
      </div>
    </div>

    <div
      v-if="state.currentBankIdState !== BankIDState.COMPLETE"
      class="flex justify-center items-center m-10 h-[4.2rem]"
    >
      <p
        v-if="openOnSameDevice"
        role="button"
        tabindex="0"
        class="text-text-link underline hover:text-text-link-hover"
        @click="changeModeShowQr"
        @keyup.enter.self.prevent="changeModeShowQr"
        @keyup.space.self.prevent="changeModeShowQr"
      >
        Använd BankID på en annan enhet
      </p>
      <p
        v-else
        role="button"
        tabindex="0"
        class="text-text-link underline hover:text-text-link-hover"
        @click="changeMode"
        @keyup.enter.self.prevent="changeMode"
        @keyup.space.self.prevent="changeMode"
      >
        Använd BankID på den här enheten
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { promiseTimeout } from '@vueuse/core';

const props = defineProps<{
  isSign?: boolean;
  openImmediately?: boolean;
  customErrorMessage?: string;
  store?: string;
}>();
const bankIdHelpDescTexts: string[] = [
  'Öppna BankID-appen på en annan enhet',
  'Tryck på “Skanna QR-kod” i BankID-appen',
  'Rikta kameran mot QR-koden nedanför',
];

const { $api } = useNuxtApp();
const { isMobileOrTablet } = useDevice();

const emit = defineEmits(['onComplete']);

const openOnSameDevice = ref(false);

const bankIdRef = ref('');
const state = useMyBankIdStore();
const showError = ref(false);
const qrData = ref('');
const qrSectionRef = ref<HTMLDivElement | null>(null);

const isCompleted = ref(false);
const isUnmounted = ref(false);
const pollingStartedAt = ref<number | null>(null);
const isCollectActive = ref(false);
const isStartPending = ref(false);

// Timeout to stop calling Collect a few seconds before BankId's own timeout
const POLLING_TIMEOUT_MS = (3 * 60 - 3) * 1000;

onMounted(() => {
  if (props.openImmediately) {
    showQr();
  }

  openOnSameDevice.value = isMobileOrTablet;
});

onUnmounted(() => {
  isUnmounted.value = true;
  cancelOrder();
});

watch(
  () => state.currentBankIdState,
  (newState) => {
    if (
      newState === BankIDState.OUTSTANDING_TRANSACTION &&
      !openOnSameDevice.value
    ) {
      anchorFocusToMiddleOfQrSection();
    }
  },
);

const showQr = () => {
  if (props.isSign) {
    sign();
  } else {
    auth();
  }
};

const autostart = () => {
  if (isStartPending.value) {
    return;
  }

  isStartPending.value = true;

  $api.bankId
    .autostart()
    .then((res) => {
      isStartPending.value = false;
      cancelOrder();
      isCompleted.value = false;
      isCollectActive.value = false;
      pollingStartedAt.value = Date.now();
      bankIdRef.value = res.OrderReference;
      state.currentBankIdState = BankIDState.OUTSTANDING_TRANSACTION;
      start();
    })
    .catch(handleError);
};

const changeMode = () => {
  cancelOrder();
  openOnSameDevice.value = !openOnSameDevice.value;
};

const changeModeShowQr = () => {
  cancelOrder();
  if (props.isSign) {
    sign();
  } else {
    auth();
  }
  openOnSameDevice.value = !openOnSameDevice.value;
};

const cancelOrder = () => {
  isCollectActive.value = false;
  isStartPending.value = false;
  state.currentBankIdState = BankIDState.NO_CLIENT;
  if (!bankIdRef.value) {
    return;
  }

  $api.bankId.cancel(bankIdRef.value).then(() => {
    state.currentBankIdState = BankIDState.NO_CLIENT;
  });

  bankIdRef.value = '';
};

const auth = () => {
  if (isStartPending.value) {
    return;
  }

  cancelOrder();
  isCompleted.value = false;
  isCollectActive.value = false;
  isStartPending.value = true;
  pollingStartedAt.value = Date.now();
  state.currentBankIdState = BankIDState.OUTSTANDING_TRANSACTION;
  $api.bankId
    .authenticate()
    .then((res) => {
      isStartPending.value = false;
      qrData.value = res.QrData;
      bankIdRef.value = res.OrderReference;
      start();
    })
    .catch(handleError);
};

const sign = () => {
  if (isStartPending.value) {
    return;
  }

  cancelOrder();
  isCompleted.value = false;
  isCollectActive.value = false;
  isStartPending.value = true;
  pollingStartedAt.value = Date.now();
  state.currentBankIdState = BankIDState.OUTSTANDING_TRANSACTION;
  $api.bankId
    .sign()
    .then((res) => {
      isStartPending.value = false;
      qrData.value = res.QrData;
      bankIdRef.value = res.OrderReference;
      start();
    })
    .catch(handleError);
};

const collect = () => {
  isCollectActive.value = false;

  if (!bankIdRef.value || isCompleted.value || isUnmounted.value) {
    return;
  }

  $api.bankId
    .collect(bankIdRef.value)
    .then(async (res) => {
      if (isCompleted.value || isUnmounted.value) {
        return;
      }

      switch (res.StatusDescription) {
        case 'OUTSTANDING_TRANSACTION':
          if (!res.QrData) {
            await handleError('QR code data error');
            break;
          }

          qrData.value = res.QrData;
          state.currentBankIdState = BankIDState.OUTSTANDING_TRANSACTION;
          start();
          break;
        case 'USER_SIGN':
          state.currentBankIdState = BankIDState.USER_SIGN;
          start();
          break;
        case 'COMPLETE':
          isCompleted.value = true;
          state.currentBankIdState = BankIDState.COMPLETE;
          qrData.value = '';
          bankIdRef.value = '';
          emit('onComplete', { bankIdCollectResponse: res });
          break;
        case 'USER_CANCEL':
          await handleError();
          break;
        case 'NO_CLIENT':
          state.currentBankIdState = BankIDState.NO_CLIENT;
          start();
          break;
        case 'STARTED':
          state.currentBankIdState = BankIDState.STARTED;
          start();
          break;
        case 'USER_REQ':
          state.currentBankIdState = BankIDState.USER_REQ;
          start();
          break;
        default:
          start();
          break;
      }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .catch(async (e: any) => {
      if (isCompleted.value || isUnmounted.value) {
        return;
      }

      await handleError(e.message);
    });
};

async function handleError(errorReason?: string) {
  isCollectActive.value = false;
  isStartPending.value = false;
  state.currentBankIdState = BankIDState.USER_CANCEL;
  bankIdRef.value = '';
  showError.value = true;

  await $api.checkout.logCheckoutData({
    store: props.store ?? 'Webshop',
    step: 'BankId Verification',
    message: errorReason ?? 'Unknown error',
  });
}

function start() {
  if (isCompleted.value || isUnmounted.value) {
    return;
  }

  if (isCollectActive.value) {
    return;
  }

  if (
    pollingStartedAt.value &&
    Date.now() - pollingStartedAt.value > POLLING_TIMEOUT_MS
  ) {
    handleError();
    return;
  }

  isCollectActive.value = true;
  promiseTimeout(1000).then(() => collect());
}

async function anchorFocusToMiddleOfQrSection() {
  await nextTick();

  const el = qrSectionRef.value;

  if (!el) {
    return;
  }

  el.focus({ preventScroll: true });

  const rect = el.getBoundingClientRect();
  const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
  if (!isFullyVisible) {
    el.scrollIntoView({ block: 'center', behavior: 'instant' });
  }
}
</script>

<style scoped></style>
